(() => {
  if (customElements.get('ab-product-studio')) return;
  class ABProductStudio extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const eventOptions = { signal: this.controller.signal };
      this.addEventListener('click', event => {
        const close = event.target.closest('[data-ab-close]');
        if (close) close.closest('dialog').close();
        const point = event.target.closest('[data-ab-measure]');
        if (point) {
          this.measure = point.dataset.abMeasure;
          this.querySelectorAll('[data-ab-measure]').forEach(button => button.setAttribute('aria-pressed', String(button === point)));
          this.querySelectorAll('[data-ab-measure-copy]').forEach(copy => { copy.hidden = copy.dataset.abMeasureCopy !== this.measure; });
          this.drawModel();
        }
        const unit = event.target.closest('[data-ab-unit]');
        if (unit) {
          this.querySelectorAll('[data-ab-unit]').forEach(button => button.setAttribute('aria-pressed', String(button === unit)));
          this.querySelectorAll('[data-ab-cm]').forEach(cell => {
            const cm = Number(cell.dataset.abCm);
            cell.textContent = cm > 0 ? (unit.dataset.abUnit === 'in' ? (cm / 2.54).toFixed(1) : String(cm)) : '—';
          });
        }
        const photo = event.target.closest('[data-ab-photo]');
        if (photo) {
          const image = this.querySelector('[data-ab-detail-photo] > img');
          image.removeAttribute('srcset');
          image.src = photo.dataset.abPhoto;
          this.querySelectorAll('[data-ab-photo]').forEach(button => button.setAttribute('aria-pressed', String(button === photo)));
        }
        const zoom = event.target.closest('[data-ab-zoom]');
        if (zoom) {
          const active = this.querySelector('[data-ab-detail-photo]').classList.toggle('is-zoomed');
          zoom.setAttribute('aria-pressed', String(active));
          zoom.querySelector('span').textContent = active ? '−' : '+';
        }
      }, eventOptions);
      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-ab-studio-open]');
        if (!trigger || trigger.dataset.abProduct !== this.dataset.product) return;
        const kind = { fit: 'Fit', size: 'Size' }[trigger.dataset.abStudioOpen] || 'Care';
        const dialog = this.querySelector(`#AB${kind}-${this.dataset.product}`);
        if (!dialog || dialog.open) return;
        this.opener = trigger;
        dialog.showModal();
        document.documentElement.classList.add('ab-studio-open');
        dialog.scrollTop = 0;
        if (kind === 'Fit') { this.loadModel(); requestAnimationFrame(() => this.drawModel()); }
      }, eventOptions);
      // The size guide button is hidden until this product has a matching size guide dialog.
      if (this.querySelector(`#ABSize-${this.dataset.product}`)) {
        document.querySelectorAll(`[data-ab-studio-open="size"][data-ab-product="${this.dataset.product}"]`).forEach(button => { button.hidden = false; });
      }
      this.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('close', () => {
          if (!document.querySelector('.ab-studio-dialog[open]')) document.documentElement.classList.remove('ab-studio-open');
          this.opener?.focus({ preventScroll: true });
        }, eventOptions);
        dialog.addEventListener('click', event => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
        }, eventOptions);
      });
      const imageArea = this.querySelector('[data-ab-detail-photo]');
      imageArea?.addEventListener('pointermove', event => {
        if (event.pointerType !== 'mouse' || !imageArea.classList.contains('is-zoomed')) return;
        const rect = imageArea.getBoundingClientRect();
        imageArea.style.setProperty('--zoom-x', `${(event.clientX - rect.left) / rect.width * 100}%`);
        imageArea.style.setProperty('--zoom-y', `${(event.clientY - rect.top) / rect.height * 100}%`);
      }, eventOptions);
      this.angle = 0;
      this.measure = 'chest';
      this.canvas = this.querySelector('[data-ab-mannequin]');
      this.rotation = this.querySelector('[data-ab-rotate]');
      if (!this.canvas || !this.rotation) return;
      this.rotation.addEventListener('input', () => { this.angle = Number(this.rotation.value); this.drawModel(); }, eventOptions);
      let pointer = null;
      this.canvas.addEventListener('pointerdown', event => {
        pointer = { x: event.clientX, angle: this.angle, id: event.pointerId };
        this.canvas.setPointerCapture(event.pointerId);
      }, eventOptions);
      this.canvas.addEventListener('pointermove', event => {
        if (!pointer || pointer.id !== event.pointerId) return;
        this.angle = Math.max(-180, Math.min(180, Math.round((pointer.angle + (event.clientX - pointer.x) * .65) / 45) * 45));
        this.rotation.value = String(Math.round(this.angle));
        this.drawModel();
      }, eventOptions);
      const release = () => { pointer = null; };
      this.canvas.addEventListener('pointerup', release, eventOptions);
      this.canvas.addEventListener('pointercancel', release, eventOptions);
      this.resizeObserver = new ResizeObserver(() => { if (this.canvas.closest('dialog').open) this.drawModel(); });
      this.resizeObserver.observe(this.canvas);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      if (!document.querySelector('.ab-studio-dialog[open]')) document.documentElement.classList.remove('ab-studio-open');
    }
    loadModel() {
      if (this.modelAtlas) return;
      this.modelAtlas = new Image();
      this.modelAtlas.decoding = 'async';
      this.modelAtlas.onload = () => {
        this.querySelector('[data-ab-model-status]').hidden = true;
        this.rotation.disabled = false;
        this.drawModel();
      };
      this.modelAtlas.onerror = () => {
        const status = this.querySelector('[data-ab-model-status]');
        status.textContent = status.dataset.error;
        this.rotation.disabled = true;
      };
      this.modelAtlas.src = this.canvas.dataset.modelAtlas;
    }
    drawModel() {
      const canvas = this.canvas;
      if (!canvas || !canvas.closest('dialog').open) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const width = canvas.clientWidth, height = canvas.clientHeight;
      if (!width || !height) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
      if (!this.modelAtlas?.complete || !this.modelAtlas.naturalWidth) return;
      const viewAngle = Math.round(this.angle / 45) * 45;
      const frame = ((viewAngle / 45) % 8 + 8) % 8;
      const centres = [181,484,777,1093,182,484,785,1093];
      const sourceWidth = 300, sourceHeight = 616;
      const bodyHeight = Math.min(height - 22, width * 1.72);
      const bodyWidth = bodyHeight * sourceWidth / sourceHeight;
      const left = (width - bodyWidth) / 2;
      const top = (height - bodyHeight) / 2;
      const centre = width / 2;
      const measurements = {
        chest: { y: .317, radius: .154, depth: .082 },
        waist: { y: .475, radius: .134, depth: .074 },
        hips: { y: .566, radius: .138, depth: .075 },
        head: { y: .088, radius: .055, depth: .063 }
      };
      const ring = measurements[this.measure];
      const drawRing = behind => {
        if (!ring) return;
        const turn = viewAngle * Math.PI / 180;
        const radius = bodyHeight * Math.hypot(ring.radius * Math.cos(turn),ring.depth * Math.sin(turn));
        ctx.save();
        ctx.beginPath();
        ctx.ellipse(centre,top + bodyHeight * ring.y,radius,bodyHeight * .013,0,behind ? Math.PI : 0,behind ? Math.PI * 2 : Math.PI);
        ctx.strokeStyle = behind ? '#617644' : '#c1d6a0';
        ctx.lineWidth = behind ? 1.2 : 2;
        if (behind) ctx.setLineDash([3,3]);
        else { ctx.shadowColor = 'rgba(33,48,19,.8)';ctx.shadowBlur = 2; }
        ctx.stroke();
        ctx.restore();
      };
      ctx.save();
      ctx.fillStyle='rgba(47,54,34,.06)';
      ctx.beginPath();
      ctx.ellipse(centre,top+bodyHeight*.985,bodyWidth*.37,5,0,0,Math.PI*2);
      ctx.fill();
      ctx.restore();
      drawRing(true);
      ctx.drawImage(this.modelAtlas,centres[frame]-sourceWidth/2,frame<4?5:632,sourceWidth,sourceHeight,left,top,bodyWidth,bodyHeight);
      drawRing(false);
      if (this.measure === 'inseam') {
        const x=centre+Math.sin(viewAngle*Math.PI/180)*bodyHeight*.018;
        ctx.save();ctx.strokeStyle='#c1d6a0';ctx.shadowColor='rgba(33,48,19,.8)';ctx.shadowBlur=2;ctx.lineWidth=2;
        ctx.beginPath();ctx.moveTo(x,top+bodyHeight*.589);ctx.lineTo(x,top+bodyHeight*.943);ctx.stroke();
        for(const y of [.589,.943]){ctx.beginPath();ctx.moveTo(x-4,top+bodyHeight*y);ctx.lineTo(x+4,top+bodyHeight*y);ctx.stroke();}
        ctx.restore();
      }
      this.querySelector('[data-ab-angle]').textContent=`${viewAngle}°`;
    }
  }
  customElements.define('ab-product-studio', ABProductStudio);
})();
